import './profile.scss';
import { FC, useEffect } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { IProfile, IStore2, popupIdsOptions } from 'types';
import {
  getProfileInformation as getProfile,
  onFollowUserToggle as followUser,
} from 'store/profile/ProfileActions';
import { PopupSlicer } from 'store/popup/PopupActions';
import { ROUTES } from 'constant';
import { UserSelector } from 'store/user/UserSelector';
import { updateAvatar as onAvatarUpdate, UserSlicer } from 'store/user/UserActions';
import Header from './header/Header';
import AnalyticsSection from './analytics/AnalyticsSection';
import GroupsSection from './groupsSection/GroupsSection';
import { uploadImage } from 'api';
import ActivitiesSection from './activitiesSection/ActivitiesSection';
import ShopSuggestionsSection from './shopSuggestionsSection/ShopSuggestionsSection';
import { ProfileSelector } from 'store/profile/ProfileSelector';
import { buildChatUrl, isEmptyArray } from 'utils';
import CompleteShopBox from 'components/completeShopBox/CompleteShopBox';
import { CreateShopSelector } from 'store/createShop/CreateShopSelector';
import { getUserFeed as getFeed } from 'store/feed/FeedActions';
import { FeedSelector } from 'store/feed/FeedSelectors';
import CreateShopBox from './createShopBox/CreateShopBox';
import { FeedSlicer } from 'store/feed/FeedSlicer';

const ProfilePage: FC<IProfile> = ({
  token,
  shouldDisplayLottie,
  avatar,
  getProfileInformation,
  totalFollowers,
  bio,
  name,
  isCreatePostLoading,
  totalCompletedDraftSteps,
  totalFollowing,
  loggedInUserId,
  isFollowing,
  isProfileOwner,
  totalProfileViews,
  onFollowToggle,
  isPremium,
  postsDetails,
  username,
  groups,
  show,
  updateAvatar,
  onAvatarLoading,
  resetUserFeed,
  isAvatarLoading,
  suggestedShops,
  getUserFeed,
  isSeller,
}) => {
  const params = useParams();

  const navigate = useNavigate();

  const userId = params?.userId;

  const shouldShowDraft = totalCompletedDraftSteps > 0 && isProfileOwner;

  const shouldShowCreateShop = !isSeller && isProfileOwner && !shouldShowDraft;

  const onEdit = () => show({ id: popupIdsOptions.editProfilePopup });

  const onAvatarChange = async (file: File) => {
    onAvatarLoading();

    const image = await uploadImage({ file });
    updateAvatar({ avatar: image });
  };

  const onViewsClick = () => show({ id: popupIdsOptions.profileViewsPopup });

  const onShopClick = (shopId: string) => navigate(`/${ROUTES.SHOP}/${shopId}`);

  const onMessageClick = () => {
    if (userId) {
      const chatDetails = {
        userId,
        username,
        avatar,
        fullName: name,
      };

      const url = buildChatUrl(chatDetails);
      navigate(url);
    }
  };

  const getUserPosts = () => {
    if (token && userId) {
      getUserFeed({ token, userId });
    }
  };

  const isLoggedInUserProfile = loggedInUserId === userId;

  const onFollowsToggle = () => {
    if (userId) {
      onFollowToggle({ userId, isFollowing });
    }
  };

  useEffect(() => {
    if (userId) {
      getProfileInformation(userId);
      getUserPosts();
    }

    return () => {
      resetUserFeed();
    };
  }, [userId, token]);

  return (
    <div className="profile-page-container page">
      <div className="preview column">
        <Header
          shouldDisplayLottie={shouldDisplayLottie}
          isLoading={isAvatarLoading}
          isPremium={isPremium}
          isLoggedInUser={isLoggedInUserProfile}
          totalFollowing={totalFollowing}
          name={name}
          isOnline
          username={username}
          onEdit={onEdit}
          onMessageClick={onMessageClick}
          onFollowToggle={onFollowsToggle}
          onAvatarChange={onAvatarChange}
          avatar={avatar}
          bio={bio}
          totalFollowers={totalFollowers}
          isFollowing={isFollowing}
        />
        <div className="profile-details">
          <div className="side-bar">
            {shouldShowDraft && (
              <CompleteShopBox
                step={totalCompletedDraftSteps}
                shouldShowLater={false}
                onLaterClick={() => {}}
                size="large"
              />
            )}
            {shouldShowCreateShop && <CreateShopBox />}
            {isLoggedInUserProfile && (
              <>
                <AnalyticsSection
                  totalViews={totalProfileViews}
                  totalSaves={0}
                  onSavesClick={() => {}}
                  onViewsClick={onViewsClick}
                />
                <GroupsSection groups={groups} onGroupsClick={onMessageClick} />
              </>
            )}
            {!isEmptyArray(suggestedShops) && (
              <ShopSuggestionsSection
                shops={suggestedShops}
                onShopClick={onShopClick}
                onFollowToggle={onFollowToggle}
              />
            )}
          </div>
          <div className="inner-preview">
            <ActivitiesSection
              postsDetails={postsDetails}
              isLoggedInUserProfile={isLoggedInUserProfile}
              isSeller={isSeller}
              isCreatePostLoading={isCreatePostLoading}
              getUserFeed={getUserPosts}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: IStore2) => {
  const {
    bio,
    totalFollowers,
    isFollowing,
    name,
    totalFollowing,
    groups,
    isPremium,
    username,
    suggestedShops,
    status,
    userId,
  } = state.profile;

  const { isLoading: isCreatePostLoading } = state.feed.createPostDetails;

  const loggedInUserId = UserSelector.loggedUserIdSelector(state);

  const isProfileOwner = userId === loggedInUserId;

  const avatar = ProfileSelector.defaultAvatarSelector(state);

  const token = UserSelector.tokenSelector(state);

  const totalCompletedDraftSteps = CreateShopSelector.totalDraftStepsCompletedSelector(state);

  const postsDetails = FeedSelector.profilePostsSelector(state);

  const { isAvatarLoading, totalProfileViews } = state.user;

  const shouldDisplayLottie = !state.user.avatar;

  const { loggedInUserShopId } = state.user;

  const isSeller = !!loggedInUserShopId;

  return {
    avatar,
    totalFollowers,
    totalProfileViews,
    isFollowing,
    bio,
    totalFollowing,
    isPremium,
    name,
    loggedInUserId,
    groups,
    isAvatarLoading,
    username,
    suggestedShops,
    shouldDisplayLottie,
    status,
    isSeller,
    isCreatePostLoading,
    isProfileOwner,
    totalCompletedDraftSteps,
    token,
    postsDetails,
  };
};

const mapDispatchToProps = {
  getProfileInformation: getProfile,
  onFollowToggle: followUser,
  resetUserFeed: FeedSlicer.actions.resetUserFeed,
  updateAvatar: onAvatarUpdate,
  show: PopupSlicer.actions.show,
  onAvatarLoading: UserSlicer.actions.onAvatarLoading,
  updateFeedType: FeedSlicer.actions.updateSelectedFeedType,
  getUserFeed: getFeed,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProfilePage);
